import { motion } from "framer-motion";
import { Mail, MapPin, Phone, Star } from "lucide-react";
import { WhatsAppIcon } from "../components/icons";
import { BRAND } from "../data/site";

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  show: { opacity: 1, y: 0 },
};

export default function ContactUs() {
  return (
    <div data-testid="contact-page" className="pt-[72px]">
      <section className="noise-dark bg-navy py-20 lg:py-28">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <p className="text-xs font-bold uppercase tracking-[0.25em] text-gold">
            Contact Us
          </p>
          <h1 className="mt-5 max-w-3xl font-display text-4xl font-extrabold tracking-tighter text-white sm:text-5xl lg:text-6xl">
            Let&apos;s plan your perfect trip.
          </h1>
          <p className="mt-6 max-w-2xl text-base leading-relaxed text-white/80 sm:text-lg">
            Call, WhatsApp or drop by — Sagar personally responds to every
            enquiry, with 24/7 support once your trip begins.
          </p>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8 lg:py-24">
        <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {[
            {
              icon: Phone,
              title: "Call Us",
              main: BRAND.phoneDisplay,
              href: BRAND.phoneHref,
              testid: "contact-call-card",
            },
            {
              icon: null,
              title: "WhatsApp",
              main: "Chat instantly",
              href: BRAND.whatsapp,
              testid: "contact-whatsapp-card",
            },
            {
              icon: Mail,
              title: "Email",
              main: BRAND.email,
              href: `mailto:${BRAND.email}`,
              testid: "contact-email-card",
            },
            {
              icon: MapPin,
              title: "Visit Us",
              main: BRAND.address,
              href: BRAND.maps,
              testid: "contact-address-card",
            },
          ].map((c, i) => (
            <motion.a
              key={c.title}
              href={c.href}
              target={c.title === "WhatsApp" || c.title === "Visit Us" ? "_blank" : undefined}
              rel="noopener noreferrer"
              variants={fadeUp}
              initial="hidden"
              whileInView="show"
              viewport={{ once: true, amount: 0.15 }}
              transition={{ duration: 0.4, delay: i * 0.07 }}
              data-testid={c.testid}
              className="rounded-2xl border border-black/5 bg-white p-7 transition-shadow duration-200 hover:shadow-xl"
            >
              <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-gold/15 text-gold-deep">
                {c.icon ? (
                  <c.icon className="h-6 w-6" />
                ) : (
                  <WhatsAppIcon className="h-6 w-6" />
                )}
              </div>
              <h2 className="mt-5 font-display text-lg font-bold text-navy">
                {c.title}
              </h2>
              <p className="mt-1.5 text-sm leading-relaxed text-gray-600">
                {c.main}
              </p>
            </motion.a>
          ))}
        </div>

        <motion.div
          variants={fadeUp}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, amount: 0.15 }}
          transition={{ duration: 0.5 }}
          data-testid="contact-rating-card"
          className="mt-10 flex flex-col items-center gap-6 rounded-3xl border border-black/5 bg-white p-8 text-center shadow-sm sm:flex-row sm:justify-between sm:p-10 sm:text-left"
        >
          <div className="flex items-center gap-6">
            <div className="font-display text-6xl font-extrabold tracking-tighter text-navy">
              {BRAND.googleRating}
            </div>
            <div>
              <div className="relative flex w-fit">
                <div className="flex gap-1">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="h-6 w-6 text-gray-300" />
                  ))}
                </div>
                <div
                  className="absolute inset-0 flex gap-1 overflow-hidden"
                  style={{ width: "94%" }}
                >
                  {[...Array(5)].map((_, i) => (
                    <Star
                      key={i}
                      data-testid="contact-google-star"
                      className="h-6 w-6 shrink-0 fill-gold text-gold"
                    />
                  ))}
                </div>
              </div>
              <p className="mt-2 text-sm font-bold uppercase tracking-[0.15em] text-gray-500">
                Rated on Google
              </p>
              <a
                href={BRAND.maps}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="contact-google-reviews-link"
                className="mt-1 text-sm font-semibold text-navy underline decoration-gold decoration-2 underline-offset-4 transition-colors duration-200 hover:text-gold-deep"
              >
                Read our reviews on Google
              </a>
            </div>
          </div>
          <a
            href={BRAND.maps}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="contact-maps-link"
            className="flex items-center gap-2 rounded-full bg-navy px-7 py-3.5 font-display font-bold text-white transition-transform duration-200 hover:scale-[1.03]"
          >
            <MapPin className="h-5 w-5 text-gold" />
            Find Us on Google Maps
          </a>
        </motion.div>

        <motion.div
          variants={fadeUp}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, amount: 0.15 }}
          transition={{ duration: 0.5 }}
          className="mt-10 overflow-hidden rounded-3xl ring-1 ring-black/5"
        >
          <iframe
            title="Manvi Travels location map"
            src="https://www.google.com/maps?q=Manvi%20Travels%2C%20KK%20Grande%2C%20Sindhi%20Society%2C%20Chembur%2C%20Mumbai%20400071&output=embed"
            className="h-[380px] w-full border-0"
            loading="lazy"
            data-testid="contact-map-embed"
          />
        </motion.div>

        <div className="mt-10 rounded-3xl bg-gold px-6 py-14 text-center sm:px-12">
          <h2 className="mx-auto max-w-xl font-display text-3xl font-extrabold tracking-tight text-navy sm:text-4xl">
            Your holiday is one message away.
          </h2>
          <a
            href={BRAND.whatsapp}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="contact-whatsapp-cta"
            className="mt-7 inline-flex items-center gap-2 rounded-full bg-navy px-8 py-4 font-display font-bold text-white transition-transform duration-200 hover:scale-[1.03]"
          >
            <WhatsAppIcon className="h-5 w-5" />
            WhatsApp Sagar Now
          </a>
        </div>
      </section>
    </div>
  );
}
